import { Controller } from "@hotwired/stimulus"

// Handles the Send E-mail modal functionality
export default class extends Controller {
  static targets = [
    "emailType",
    "sendTo",
    "emailContent",
    "includeRecap",
    "activityId",
    "emailInput",
    "tagsContainer"
  ]

  static values = {
    inloopId: Number,
    sendUrl: String
  }

  additionalEmails = []

  // Email templates for different types
  templates = {
    invite: `Hi {participant_name},

You're invited to join our workshop!

Click the link below to get started:
{join_link}

Looking forward to your participation!

Best regards`,
    reminder: `Hi {participant_name},

This is a friendly reminder about our workshop.

If you haven't already, please click the link below to join:
{join_link}

We'd love to hear your thoughts!

Best regards`,
    thank_you: `Hi {participant_name},

Thank you for participating in our workshop!

Your insights have been incredibly valuable. We appreciate you taking the time to share your thoughts with us.

Best regards`
  }

  connect() {
    // Initialize additional emails array
    this.additionalEmails = []

    // Set initial template
    this.updateTemplate()

    // Listen for select component changes
    if (this.hasEmailTypeTarget) {
      this.emailTypeTarget.addEventListener('change', () => {
        this.updateTemplate()
      })
    }

    // Make the controller accessible globally for inter-controller communication
    this.element.sendEmailController = this
  }

  // Update email content based on selected type
  updateTemplate() {
    if (!this.hasEmailTypeTarget || !this.hasEmailContentTarget) return

    // For Select component, check if it's a hidden input or regular select
    const emailTypeEl = this.emailTypeTarget
    const type = emailTypeEl.type === 'hidden'
      ? emailTypeEl.value
      : emailTypeEl.value

    const template = this.templates[type] || this.templates.invite

    this.emailContentTarget.value = template
  }

  // Email tags handling
  handleEmailKeydown(event) {
    const input = event.target
    const value = input.value.trim()

    // Handle Enter or Comma
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      if (value) {
        this.addEmail(value)
        input.value = ""
      }
    }

    // Handle Backspace on empty input - remove last tag
    if (event.key === "Backspace" && !value && this.additionalEmails.length > 0) {
      this.additionalEmails.pop()
      this.renderTags()
    }
  }

  handleEmailBlur(event) {
    const value = event.target.value.trim()
    if (value) {
      this.addEmail(value)
      event.target.value = ""
    }
  }

  addEmail(email) {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (emailRegex.test(email) && !this.additionalEmails.includes(email)) {
      this.additionalEmails.push(email)
      this.renderTags()
    } else if (!emailRegex.test(email)) {
      this.showToast("Invalid email format", "error")
    }
  }

  removeEmail(event) {
    const email = event.currentTarget.closest('[data-email]').dataset.email
    this.additionalEmails = this.additionalEmails.filter(e => e !== email)
    this.renderTags()
  }

  renderTags() {
    if (!this.hasTagsContainerTarget) return

    const tagsHTML = this.additionalEmails.map(email => `
      <span class="inline-flex items-center gap-1 px-2 py-1 bg-[#F2F3F5] rounded-full text-xs font-medium text-[#374151]"
            data-email="${email}">
        ${email}
        <button type="button" class="hover:text-[#B42318]" data-action="click->send-email#removeEmail">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6L18 18"/>
          </svg>
        </button>
      </span>
    `).join('')

    this.tagsContainerTarget.innerHTML = tagsHTML
  }

  focusEmailInput() {
    if (this.hasEmailInputTarget) {
      this.emailInputTarget.focus()
    }
  }

  // Send individual invite
  async sendInvite() {
    const email = this.hasInviteEmailTarget ? this.inviteEmailTarget.value.trim() : ""
    const activityId = this.hasActivityIdTarget ? this.activityIdTarget.value : null

    if (!email) {
      this.showToast("Please enter an email address", "error")
      return
    }

    if (!activityId) {
      this.showToast("Please select an activity", "error")
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      this.showToast("Please enter a valid email address", "error")
      return
    }

    try {
      this.showToast("Sending invitation...", "success")

      const response = await fetch(this.sendUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          email_type: "invite",
          send_to: "selected",
          selected_emails: [email],
          activity_id: activityId,
          content: this.templates.invite
        })
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast("Invitation sent successfully!", "success")
        this.inviteEmailTarget.value = ""
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        this.showToast(data.error || "Failed to send invitation", "error")
      }
    } catch (error) {
      console.error("Send invite error:", error)
      this.showToast("Network error. Please try again.", "error")
    }
  }

  // Send emails to participants
  async sendEmails() {
    const emailType = this.hasEmailTypeTarget ? this.emailTypeTarget.value : "invite"
    const sendTo = this.hasSendToTarget ? this.sendToTarget.value : "none"
    const content = this.hasEmailContentTarget ? this.emailContentTarget.value : ""
    const includeRecap = this.hasIncludeRecapTarget ? this.includeRecapTarget.checked : false
    const activityId = this.hasActivityIdTarget ? this.activityIdTarget.value : null

    if (!content.trim()) {
      this.showToast("Please enter email content", "error")
      return
    }

    if (!this.hasSendUrlValue) {
      this.showToast("Send URL not configured", "error")
      return
    }

    try {
      this.showToast("Sending emails...", "success")

      const requestBody = {
        email_type: emailType,
        send_to: sendTo,
        content: content,
        include_recap: includeRecap,
        activity_id: activityId
      }

      // Add additional emails if provided
      if (this.additionalEmails.length > 0) {
        requestBody.additional_emails = this.additionalEmails
      }

      const response = await fetch(this.sendUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast(`Emails sent to ${data.sent_count} recipient(s)!`, "success")
        // Clear additional emails
        this.additionalEmails = []
        this.renderTags()
        if (this.hasEmailInputTarget) {
          this.emailInputTarget.value = ""
        }
        this.closeModal()
        // Refresh page to show updated email logs
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        this.showToast(data.error || "Failed to send emails", "error")
      }
    } catch (error) {
      console.error("Send email error:", error)
      this.showToast("Network error. Please try again.", "error")
    }
  }

  // Close the modal
  closeModal() {
    const modal = this.element.closest("[data-inloop-participants-target='sendEmailModal']")
    if (modal) {
      modal.classList.add("hidden")

      // Clear additional emails when closing
      this.additionalEmails = []
      this.renderTags()
      if (this.hasEmailInputTarget) {
        this.emailInputTarget.value = ""
      }
    }
  }

  // Helper: Show toast notification
  showToast(message, type = "success") {
    const toast = document.createElement("div")
    toast.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg text-white z-50 ${
      type === "error" ? "bg-red-600" : "bg-green-600"
    }`
    toast.textContent = message
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  // Helper: Get CSRF token
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
